import { useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import type {
  AdminProjectionFreshness,
  AdminUserLookupInput,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import { useAdminAutoRefreshTick } from "../../shared/query/admin-auto-refresh";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { normalizeAdminQueryError } from "../../shared/query/normalize-admin-query-error";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import { toUserOperationsViewModel, type UserOperationsViewModel } from "./user-data-view-model";
import { loadUserSummary } from "./users-use-cases";

export type UserOperationsScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  lookup?: AdminUserLookupInput;
};

export type UserOperationsScreenModel = AdminScreenState<UserOperationsViewModel>;

function hasLookupValue(lookup: AdminUserLookupInput | undefined): boolean {
  return Boolean(
    lookup?.internalUserId ||
      lookup?.userIdHash ||
      lookup?.requestId ||
      lookup?.correlationId,
  );
}

function deriveFreshnessPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "stale" | "partial" | "ready" {
  const values = Object.values(sourceFreshness);

  if (values.includes("stale")) {
    return "stale";
  }

  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }

  return "ready";
}

function createDependenciesKey(dependencies: UserOperationsScreenModelDependencies): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    lookup: dependencies.lookup ?? {},
  });
}

export async function resolveUserOperationsScreenModel(
  dependencies: UserOperationsScreenModelDependencies,
): Promise<UserOperationsScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.users.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "User summary read permission denied.",
        ko: "사용자 요약 조회 권한이 없습니다.",
      },
      "admin_user_summary_permission_denied",
    );
  }

  if (!hasLookupValue(dependencies.lookup)) {
    return {
      status: "empty",
    };
  }

  try {
    const envelope = await loadUserSummary(dependencies.queryPort, dependencies.lookup ?? {});
    const viewModel = toUserOperationsViewModel(
      envelope.data,
      envelope.meta,
      dependencies.lookup ?? {},
      dependencies.locale ?? "ko",
    );
    const freshnessPriority = deriveFreshnessPriority(envelope.meta.sourceFreshness);

    if (freshnessPriority === "stale") {
      return {
        status: "stale",
        viewModel,
      };
    }

    if (freshnessPriority === "partial") {
      return {
        status: "partial",
        viewModel,
      };
    }

    return {
      status: "ready",
      viewModel,
    };
  } catch (error) {
    const normalizedError = normalizeAdminQueryError(error);

    if (normalizedError.code === "ADMIN_QUERY_USER_NOT_FOUND") {
      return {
        status: "empty",
      };
    }

    return toAdminScreenErrorState(dependencies.locale ?? "ko", normalizedError);
  }
}

export function useUserOperationsScreenModel(
  dependencies: UserOperationsScreenModelDependencies,
  refreshSignal?: number,
): UserOperationsScreenModel {
  const [state, setState] = useState<UserOperationsScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);
  const refreshTick = useAdminAutoRefreshTick(dependencies.runtimeConfig);

  useEffect(() => {
    let cancelled = false;

    setState((current) => createLoadingAdminScreenState(current.viewModel));

    void resolveUserOperationsScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependenciesKey, refreshSignal, refreshTick]);

  return state;
}
